import { cfoCurrencySymbol } from '../cfoFormat'

// Deterministic, unit-aware number formatting for the highlight visual layer.
//
// No `Intl`, no `toLocaleString`: those are ICU-dependent, so the browser and
// the Node test runner disagree, and the exported PDF can differ from the
// preview it was built from. Everything here is pure arithmetic and string
// building, which is what lets the same value hash identically everywhere.
//
// This is the ONLY formatter in the visual layer. `evidence.ts`, `scene.ts` and
// `selection.ts` all route through it, so a value cannot render one way on a
// card and another way inside its own chart.

export type HighlightUnit = 'usd' | 'pct' | 'pct_pts' | 'months' | 'days' | 'ratio' | 'count'

/** U+2212. Typographic minus — aligns with digits; the ASCII hyphen does not. */
const MINUS = '−'

const fixed = (value: number, places: number): string => {
  // toFixed rounds half away from zero on the magnitude, which is what a
  // financial reader expects, and never emits exponent notation in our range.
  const rounded = value.toFixed(places)
  // Normalize "-0" / "-0.0" to plain zero so a rounding artifact never renders
  // as a negative.
  return /^-0(\.0+)?$/.test(rounded) ? rounded.slice(1) : rounded
}

const groups = (digits: string): string => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const compactMagnitude = (abs: number): { value: string; suffix: string } => {
  if (abs >= 1_000_000_000) return { value: fixed(abs / 1_000_000_000, 1), suffix: 'B' }
  if (abs >= 1_000_000) return { value: fixed(abs / 1_000_000, 1), suffix: 'M' }
  if (abs >= 1_000) return { value: fixed(abs / 1_000, 1), suffix: 'K' }
  return { value: fixed(abs, 0), suffix: '' }
}

export type FormatOptions = {
  /** Prefix non-negative values with `+`. Use for variances, never for levels. */
  signed?: boolean
  /** Force full precision instead of K/M/B. Use in tables, not on charts. */
  exact?: boolean
}

/**
 * Format a value for display. Returns an em-dash for anything non-finite so a
 * missing number never renders as `NaN`, `null` or an accidental zero.
 */
export function formatValue(value: number | null | undefined, unit: string, options: FormatOptions = {}): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const abs = Math.abs(value)

  // Decide the sign from the ROUNDED magnitude, not the raw one. −$0.004
  // rounds to zero at display precision, and "−$0" is a number that does not
  // exist — it reads as a loss where there is none.
  const signFor = (rendered: string): string => {
    if (/^0(\.0+)?$/.test(rendered.replace(/,/g, ''))) return ''
    return value < 0 ? MINUS : options.signed ? '+' : ''
  }

  switch (unit) {
    case 'usd': {
      // `usd` is the legacy wire unit meaning "money". The workbook-level
      // currency context supplies the actual base-currency symbol so shared
      // Xero/Sage/NetSuite visuals do not silently relabel GBP or AUD as USD.
      const symbol = cfoCurrencySymbol()
      if (options.exact) {
        const rendered = fixed(abs, 0)
        return `${signFor(rendered)}${symbol}${groups(rendered)}`
      }
      const { value: magnitude, suffix } = compactMagnitude(abs)
      return `${signFor(suffix ? '1' : magnitude)}${symbol}${magnitude}${suffix}`
    }
    case 'pct': {
      const rendered = fixed(abs, abs < 10 ? 1 : 0)
      return `${signFor(rendered)}${rendered}%`
    }
    case 'pct_pts': {
      const rendered = fixed(abs, 1)
      return `${signFor(rendered)}${rendered} pts`
    }
    case 'months': {
      const rendered = fixed(abs, 1)
      return `${signFor(rendered)}${rendered} mo`
    }
    case 'days': {
      const rendered = fixed(abs, 0)
      return `${signFor(rendered)}${groups(rendered)} days`
    }
    case 'ratio': {
      const rendered = fixed(abs, 2)
      return `${signFor(rendered)}${rendered}×`
    }
    default: {
      const rendered = fixed(abs, abs < 10 && !Number.isInteger(abs) ? 1 : 0)
      return `${signFor(rendered)}${groups(rendered)}`
    }
  }
}

/**
 * The headline number on a highlight card. Always signed, because a highlight
 * is by definition a movement — an unsigned change is not a finding.
 */
export function formatHeadlineValue(value: number | null | undefined, unit: string): string {
  return formatValue(value, unit, { signed: true })
}

/** Percentage-change label, e.g. `−54%`. Null when the base makes it meaningless. */
export function formatDeltaPct(delta: number | null | undefined, base: number | null | undefined): string | null {
  if (delta == null || base == null || !Number.isFinite(delta) || !Number.isFinite(base)) return null
  // A percentage against a near-zero or sign-flipping base is not information.
  if (Math.abs(base) < 0.005) return null
  return formatValue((delta / Math.abs(base)) * 100, 'pct', { signed: true })
}
